// src/components/navbar.js
import React, { useContext } from "react";
import {
  Box,
  Flex,
  Text,
  IconButton,
  Button,
  Stack,
  Collapse,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import {
  HamburgerIcon,
  CloseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { Logo } from "./Logo";
import { scroller } from "react-scroll";
import { Link as GatsbyLink, navigate } from "gatsby";
import { LanguageContext } from "../components/LanguageProvider";
import { messages } from "../intl/config";
import PolandFlag from "../images/poland_flag.png";
import UKFlag from "../images/uk_flag.png";

const scrollToSection = (target_id) => {
  scroller.scrollTo(target_id, {
    duration: 900,
    delay: 0,
    smooth: "easeInOutQuart",
    spy: true,
    offset: -70,
    exact: true,
  });
};

const navigateAndScroll = (path, target_id) => {
  const currentPath = window.location.pathname;
  if (!target_id) {
    if (currentPath !== path) navigate(path);
    return;
  }
  if (currentPath === path) {
    scrollToSection(target_id);
    return;
  }
  navigate(path);
  setTimeout(() => scrollToSection(target_id), 200);
};

export default function WithSubnavigation() {
  const { isOpen, onToggle } = useDisclosure();
  const currentPath =
    typeof window !== "undefined" ? window.location.pathname : "/";
  const { locale = "en" } = useContext(LanguageContext) || {};

  // zawsze angielskie etykiety na blogu
  const activeTranslations = currentPath.startsWith("/blog")
    ? messages.en
    : messages[locale] || {};

  // menu
  const navItems = [
    {
      label: activeTranslations["navbar.career"],
      path: "/career",
      target_id: null,
    },
    {
      label: activeTranslations["navbar.about"], // np. "About us" / "O nas"
      path: "/about",
      target_id: null,
    },
    {
      label: "Blog",
      path: "/blog",
      target_id: null,
    },
  ];

  const changeLanguageTo = (newLocale) => {
    const without = window.location.pathname.replace(/^\/(pl|en)/, "");
    window.location.href = `/${newLocale}${without}`;
  };

  const handleContactClick = (e) => {
  e.preventDefault();
  // zachowujemy prefix języka jeśli są `/pl` lub `/en`
  const localePrefix = currentPath.startsWith("/pl")
    ? "/pl"
    : currentPath.startsWith("/en")
    ? "/en"
    : "";
  navigateAndScroll(localePrefix || "/", "pricing");
  };



  return (
    <Box
      position="fixed"
      w="100%"
      zIndex="100"
      boxShadow="sm"
      bg={useColorModeValue("white", "gray.800")}
      color={useColorModeValue("gray.600", "white")}
    >
      <Flex
        minH="60px"
        py={{ base: 2 }}
        px={{ base: 4 }}
        align="center"
        maxW="8xl"
        mx="auto"
      >
        <Flex flex={1}>
          <Box>
            <GatsbyLink to="/">
              <Logo />
            </GatsbyLink>
          </Box>
          <Flex display={{ base: "none", md: "flex" }} ml={10}>
            <DesktopNav navItems={navItems} />
          </Flex>
        </Flex>

        <Stack direction="row" spacing={3} align="center">
          {!currentPath.startsWith("/blog") && (
            <Popover placement="bottom-start" closeOnBlur>
              <PopoverTrigger>
                <Button
                  size="md"
                  fontWeight={600}
                  bg="transparent"
                  p={0}
                  _hover={{ bg: "transparent" }}
                >
                  {locale === "pl" ? (
                    <img
                      src={PolandFlag}
                      alt="Polski"
                      style={{ width: 24, border: "1px solid #000" }}
                    />
                  ) : (
                    <img
                      src={UKFlag}
                      alt="English"
                      style={{ width: 24, border: "1px solid #000" }}
                    />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent w="auto">
                <Stack>
                  <Button
                    variant="ghost"
                    onClick={() => changeLanguageTo("pl")}
                    justify="flex-start"
                  >
                    <img
                      src={PolandFlag}
                      alt="Polski"
                      style={{ width: 24, marginRight: 8, border: "1px solid #000" }}
                    />
                    Polski
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => changeLanguageTo("en")}
                    justify="flex-start"
                  >
                    <img
                      src={UKFlag}
                      alt="English"
                      style={{ width: 24, marginRight: 8, border: "1px solid #000" }}
                    />
                    English
                  </Button>
                </Stack>
              </PopoverContent>
            </Popover>
          )}

          <Button
            as="a"
            display={{ base: "none", md: "inline-flex" }}
            fontSize="md"
            fontWeight={600}
            colorScheme="purple"
            bg="purple.400"
            _hover={{ bg: "purple.500" }}
            onClick={handleContactClick}
          >
            {activeTranslations["navbar.contact"]}
          </Button>

          <IconButton
            display={{ base: "inline-flex", md: "none" }}
            onClick={onToggle}
            icon={
              isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />
            }
            variant="ghost"
            aria-label="Toggle Navigation"
          />
        </Stack>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <MobileNav navItems={navItems} />
      </Collapse>
    </Box>
  );
}

const DesktopNav = ({ navItems }) => {
  const linkColor = useColorModeValue("gray.600", "gray.200");
  const linkHoverColor = useColorModeValue("gray.800", "white");
  const popoverBg = useColorModeValue("white", "gray.800");

  return (
    <Stack direction="row" spacing={4}>
      {navItems.map((item) => (
        <Box key={item.path}>
          <Popover trigger="hover" placement="bottom-start">
            <PopoverTrigger>
              <Button
                variant="link"
                p={2}
                fontSize="md"
                fontWeight={500}
                color={linkColor}
                _hover={{ color: linkHoverColor }}
                onClick={() => navigateAndScroll(item.path, item.target_id)}
              >
                {item.label}
              </Button>
            </PopoverTrigger>
          </Popover>
        </Box>
      ))}
    </Stack>
  );
};

const MobileNav = ({ navItems }) => (
  <Stack bg={useColorModeValue("white", "gray.800")} p={4} display={{ md: "none" }}>
    {navItems.map(({ label, path, target_id }) => (
      <Button
        key={path}
        variant="ghost"
        justifyContent="flex-start"
        onClick={() => navigateAndScroll(path, target_id)}
      >
        {label}
      </Button>
    ))}
  </Stack>
);
